import React, { useContext, useEffect, useState } from "react";
import { Button, Checkbox, Col, Collapse, Divider, InputNumber, message, Modal, Row, Select, TimePicker, Typography, Switch, Tooltip } from "antd";
import './time-method-stops.style.scss';
import { getArrayfromString, isValid, isValidArray, isValidNumber } from "../../../utils/utilities";
import { getTimeStampInReadableFormat } from "../../../utils/date.utils";
import TicMarkIcon from './../../../images/tic-mark.png';
import Icon from '@ant-design/icons';
import { loadingAction } from "../../../actions/loading-actions";
import { useDispatch } from "react-redux";
import moment from "moment";
import ZoomComponent from '../../custom-components/zoom-component/zoom.component';
import { DeviceContext } from "../../device-provider/device-provider";
import {
    MinusSquareOutlined,
    MinusSquareFilled,
    PlusSquareOutlined,
    EditOutlined,
    DeleteOutlined,
    CheckSquareFilled,
    PlusOutlined,
    SaveOutlined,
    CloseOutlined
} from '@ant-design/icons';
import { getAddressLocations } from "../../../actions/trip-create.actions";
import { CreateStandardStopsPage } from "../../../pages/create-standard-stop.page";

const { Panel } = Collapse;
const { Title, Text } = Typography;
const { Option } = Select;

const TimePickerComponent = ({ value, onSelect, showError, timeExits, isMobile }) => (
    <TimePicker 
        size={isMobile ? "small" : "small"}
        className={`${showError && !timeExits ? "remove-clear-icon errorInput" : "remove-clear-icon"} ${isMobile ? "mobile-time-picker" : ""}`}
        popupClassName={isMobile ? "mobile-time-picker-popup time-picker-to-not-display-footer" : "time-picker-to-not-display-footer"}
        use12Hours 
        format='h:mm A'  
        minuteStep={5} 
        value={isValid(value) ? moment(value, 'HH:mm') : null}
        onSelect={onSelect}
        style={{ width: '100%' }}
    />
);

const StopNameDisplay = ({ displayName, isBoarding, isDrop }) => {
    return (
        <span style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap'  }}>
            {displayName || 'Unknown Stop'}
            {(isBoarding || isDrop) && (
                <span style={{ marginLeft: '4px' }}>
                    {isBoarding && (
                        <span style={{ 
                            backgroundColor: '#1890ff',
                            color: 'white',
                            padding: '0 4px',
                            borderRadius: '2px',
                            fontSize: '12px',
                            marginRight: '4px'
                        }}>
                            B
                        </span>
                    )}
                    {isDrop && (
                        <span style={{ 
                            backgroundColor: '#52c41a',
                            color: 'white',
                            padding: '0 4px',
                            borderRadius: '2px',
                            fontSize: '12px'
                        }}>
                            D
                        </span>
                    )}
                </span>
            )}
        </span>
    );
};

const CityCheckbox = ({ isActive, cityIndex, onToggle, isShowCheckBox=true }) => {
    if (!isShowCheckBox) return null;

    const handleClick = (e) => {
        e.stopPropagation();
        onToggle(!isActive, cityIndex);
    };

    if (!isActive) {
        return (
            <MinusSquareFilled 
                className="marginRight"
                style={{ 
                    color: '#ff4d4f',
                    fontSize: '18px',
                    cursor: 'pointer'
                }}
                onClick={handleClick}
            />
        );
    }

    return (
        <CheckSquareFilled 
            className="marginRight default-checkbo-btn"
            style={{ 
                fontSize: '18px',
                cursor: 'pointer'
            }}
            onClick={handleClick}
        />
    );
};

const DayInputComponent = ({ value, onChange, showError, dayExits, isMobile }) => (
    <InputNumber
        className={`${showError && !dayExits ? "dayInput errorInput" : "dayInput"} ${isMobile ? "mobile-day-input" : ""}`}
        size="small"
        value={value}
        onChange={onChange}
        style={{ width: '50px' }}
    />
);

export const TimeMethodStops = (props) => {
    const {
        selectedCities = [],
        existingStops = [],
        onStopsUpdate,
        onContinue,
        customClassName,
        // Temporary add-city flow: only the new city is editable, no trip-close toggle.
        temporaryCityFlow = false,
        temporaryCityName = null
    } = props;

    const [citiesWithStops, setCitiesWithStops] = useState([]);
    const [standardStopsWithCities, setStandardStopsWithCities] = useState({});
    const [isOpenNewStop, setIsOpenNewStop] = useState(false);
    const [selectedCity, setSelectedCity] = useState(null);
    const [selectedCityIndex, setSelectedCityIndex] = useState(null);
    const [selectedStopIndex, setSelectedStopIndex] = useState(null);
    const [showError, setShowError] = useState(false);
    const [expandedPanels, setExpandedPanels] = useState([]);
    const [savingStops, setSavingStops] = useState(new Set()); // Track which stops are being saved
    const [editingStops, setEditingStops] = useState({}); // Store changes for stops being edited

    const dispatch = useDispatch();
    const { isMobile } = useContext(DeviceContext);

    // Helper function to sort cities based on day and timing
    const sortCitiesByDayAndTiming = (citiesWithStops) => {
        // Create a flat array of all stops with their city info
        let allStops = [];
        citiesWithStops.forEach((city, cityIndex) => {
            city.stops.forEach((stop, stopIndex) => {
                allStops.push({
                    ...stop,
                    cityIndex,
                    stopIndex,
                    cityName: city.displayName,
                    isBoarding: city.isBoarding,
                    isDrop: city.isDrop,
                    isActive: city.isActive,
                    // Ensure displayName is preserved from the stop
                    displayName: stop.displayName && stop.displayName.trim() !== '' ? stop.displayName : (city.displayName || 'Unknown Stop')
                });
            });
        });

        // Sort by day first, then by timing
        allStops.sort((a, b) => {
            // First sort by day
            const dayA = isValidNumber(a.day) ? a.day : 0;
            const dayB = isValidNumber(b.day) ? b.day : 0;
            
            if (dayA !== dayB) {
                return dayA - dayB;
            }
            
            // If same day, sort by timing
            const timeA = isValid(a.timing) ? a.timing : '00:00:00';
            const timeB = isValid(b.timing) ? b.timing : '00:00:00';
            
            return timeA.localeCompare(timeB);
        });

        // Group stops back by city in the sorted order
        const sortedCities = [];
        const cityMap = new Map();

        // First, add all cities to the map to preserve cities without stops
        citiesWithStops.forEach(city => {
            cityMap.set(city.displayName, {
                city: city.city,
                displayName: city.displayName,
                isActive: city.isActive,
                isBoarding: city.isBoarding,
                isDrop: city.isDrop,
                // Preserve city-level time and day fields
                cityTime: city.cityTime,
                cityDay: city.cityDay,
                stops: []
            });
        });

        // Then add all stops to their respective cities
        allStops.forEach(stop => {
            const cityKey = stop.cityName;
            if (cityMap.has(cityKey)) {
                cityMap.get(cityKey).stops.push(stop);
            }
        });

        // Convert map to array
        cityMap.forEach(city => {
            sortedCities.push(city);
        });

        return sortedCities;
    };

    // Helper function to update expanded panels after sorting
    const updateExpandedPanelsAfterSort = (originalCities, sortedCities) => {
        // Create a mapping from city name to new index
        const cityNameToNewIndex = new Map();
        sortedCities.forEach((city, newIndex) => {
            cityNameToNewIndex.set(city.displayName, newIndex.toString());
        });

        // Update expanded panels based on city names
        const newExpandedPanels = [];
        originalCities.forEach((city, originalIndex) => {
            if (expandedPanels.includes(originalIndex.toString())) {
                const newIndex = cityNameToNewIndex.get(city.displayName);
                if (newIndex) {
                    newExpandedPanels.push(newIndex);
                }
            }
        });

        setExpandedPanels(newExpandedPanels);
    };

    // Initialize cities with stops from selected cities and existing stops
    useEffect(() => {
        if (isValidArray(selectedCities)) {
            let initialCitiesWithStops = [];

            // If existing stops are provided, use them and sort by day/timing
            if (isValidArray(existingStops) && existingStops.length > 0) {
                
                // Process existing stops and ensure they have the correct structure
                const processedExistingStops = existingStops.map(city => {
                    // Find the main stop for this city to set city-level time and day
                    const mainStop = (city.stops || []).find(stop => stop.isMainStop) || (city.stops || [])[0];
                    
                    const cityTime = mainStop ? mainStop.timing : null;
                    const cityDay = mainStop ? mainStop.day : 0;
                    
                    return {
                        ...city,
                        displayName: city.displayName || city.city || 'Unknown City',
                        // Add city-level time and day from main stop
                        cityTime: cityTime,
                        cityDay: cityDay,
                        stops: (city.stops || []).map(stop => {
                            // Determine the display name with fallback logic
                            let displayName = stop.displayName;
                            
                            // Check if displayName is empty or invalid
                            if (!displayName || displayName.trim() === '') {
                                if (stop.addressData) {
                                    displayName = stop.addressData.name || stop.addressData.location_name;
                                }
                                if (!displayName || displayName.trim() === '') {
                                    displayName = stop.name;
                                }
                                if (!displayName || displayName.trim() === '') {
                                    displayName = stop.location_name;
                                }
                                if (!displayName || displayName.trim() === '') {
                                    displayName = stop.address_name;
                                }
                                if (!displayName || displayName.trim() === '') {
                                    displayName = stop.stop_name;
                                }
                                if (!displayName || displayName.trim() === '') {
                                    displayName = 'Unknown Stop';
                                }
                            }
                            
                            return {
                                ...stop,
                                // If the stop has a selectedStopId, it's an existing stop from Excel
                                isNewStop: !stop.selectedStopId,
                                // Ensure the stop has the correct structure for existing stops
                                isActive: stop.isActive !== undefined ? stop.isActive : 1,
                                isStop: true,
                                // Use the determined display name - ensure it's not empty
                                displayName: displayName && displayName.trim() !== '' ? displayName : 'Unknown Stop',
                                // If it has addressData, use it to populate the stop details
                                ...(stop.addressData && {
                                    selectedStopId: stop.selectedStopId,
                                    // The address data is already in the correct format
                                }),
                                // Ensure we have the necessary properties for existing stops
                                ...(stop.selectedStopId && {
                                    isNewStop: false
                                })
                            };
                        })
                    };
                });
                
                initialCitiesWithStops = sortCitiesByDayAndTiming(processedExistingStops);
                
                // Auto-expand all panels when there are existing stops from Excel
                const allPanelKeys = initialCitiesWithStops.map((_, index) => index.toString());
                setExpandedPanels(allPanelKeys);
            } else {
                // Create empty cities structure without stops - user will create stops manually
                // Use the order from selected cities page
                initialCitiesWithStops = selectedCities.map((city, index) => ({
                    city: city.city_name,
                    displayName: city.city_name,
                    isActive: true,
                    isBoarding: city.isOrigin || false,
                    isDrop: city.isDestination || false,
                    cityTime: null, // City-level time - will be set when main stop is selected
                    cityDay: 0, // City-level day - will be set when main stop is selected
                    stops: [] // Empty stops array - user will add stops manually
                }));
                
                // Auto-expand all panels when there are no existing stops
                const allPanelKeys = initialCitiesWithStops.map((_, index) => index.toString());
                setExpandedPanels(allPanelKeys);
            }

            setCitiesWithStops(initialCitiesWithStops);
        }
    }, [selectedCities, existingStops]);

    const setLoading = (bool) => {
        dispatch(loadingAction(bool));
    };

    const updateStopsDate = (value, cityIndex, stopIndex, label, selectedStop = null) => {
        // Guard against a city/stop slot that no longer exists (the stops array can be
        // re-sorted/rebuilt between opening the "Create New Stop" modal and its async
        // success callback); dereferencing an undefined stop below would otherwise throw.
        const targetCity = citiesWithStops[cityIndex];
        if (!isValid(targetCity) || !isValidArray(targetCity.stops) || !isValid(targetCity.stops[stopIndex])) {
            return;
        }

        if (label === 'tripClosePoint' && value === 1) {
            // Reset all other tripClosePoints to 0
            citiesWithStops.forEach((city) => {
                city.stops.forEach((stop) => {
                    stop.tripClosePoint = 0;
                });
            });
        }

        if (label === 'selectedStopId' && citiesWithStops[cityIndex].stops[stopIndex].isNewStop) {
            if (selectedStop) {
                // Get default phone numbers from global travels phone number
                const phoneNumbers = getArrayfromString(global.travelsPhoneNumber);
                const defaultPhoneNumber = phoneNumbers[0] || null;
                const defaultLandlineNumber = phoneNumbers[1] || null;
                
                // Preserve the main stop status
                const wasMainStop = citiesWithStops[cityIndex].stops[stopIndex].isMainStop;
                                
                citiesWithStops[cityIndex].stops[stopIndex] = {
                    ...citiesWithStops[cityIndex].stops[stopIndex],
                    selectedStopId: value,
                    city: selectedStop.city,
                    displayName: selectedStop.name,
                    isActive: 1,
                    isStop: true,
                    timing: citiesWithStops[cityIndex].stops[stopIndex].timing || null,
                    tripClosePoint: selectedStop.tripClosePoint || 0,
                    isBoarding: citiesWithStops[cityIndex].isBoarding || false,
                    isDrop: citiesWithStops[cityIndex].isDrop || false,
                    phoneNumber: isValid(selectedStop.data.phone_number) ? selectedStop.data.phone_number : defaultPhoneNumber,
                    landlineNumber: isValid(selectedStop.data.landline_number) ? selectedStop.data.landline_number : defaultLandlineNumber,
                    isMainStop: wasMainStop, // Preserve main stop status
                    isNewStop: true // Keep it as new stop until user clicks save
                };
                
                // Update city-level time and day if this is the main stop
                if (wasMainStop) {
                    updateCityTimeAndDay(cityIndex, citiesWithStops[cityIndex].stops[stopIndex]);
                }
                
                // Don't sort immediately when selecting a stop - only update state
                setCitiesWithStops([...citiesWithStops]);
            }
        } else {
            citiesWithStops[cityIndex].stops[stopIndex][label] = value;
            
            // Update city-level time and day if this is the main stop and timing/day was updated
            if (citiesWithStops[cityIndex].stops[stopIndex].isMainStop && (label === 'timing' || label === 'day')) {
                updateCityTimeAndDay(cityIndex, citiesWithStops[cityIndex].stops[stopIndex]);
            }
            
            // Sort cities if day or timing was updated, but only for existing stops that are not being edited
            if (label === 'day' || label === 'timing') {
                const isNewStop = citiesWithStops[cityIndex].stops[stopIndex].isNewStop;
                const isBeingEdited = citiesWithStops[cityIndex].stops[stopIndex].isEdit;
                
                if (!isNewStop && !isBeingEdited) {
                    // Only sort for existing stops that are not being edited
                    const sortedCities = sortCitiesByDayAndTiming(citiesWithStops);
                    setCitiesWithStops(sortedCities);
                    updateExpandedPanelsAfterSort(citiesWithStops, sortedCities);
                } else {
                    // For new stops or stops being edited, just update the state without sorting
                    setCitiesWithStops([...citiesWithStops]);
                }
            } else {
                setCitiesWithStops([...citiesWithStops]);
            }
        }
    };

    // Function to update city-level time and day from main stop
    const updateCityTimeAndDay = (cityIndex, mainStop) => {
        if (mainStop && mainStop.isMainStop) {
            citiesWithStops[cityIndex].cityTime = mainStop.timing;
            // Ensure day is set even if it's 0 (which is a valid day value)
            citiesWithStops[cityIndex].cityDay = mainStop.day !== undefined && mainStop.day !== null ? mainStop.day : 0;
        }
    };

    // Function to update editing state for stops being edited
    const updateEditingStop = (cityIndex, stopIndex, field, value) => {
        const stopKey = `${cityIndex}-${stopIndex}`;
        setEditingStops(prev => ({
            ...prev,
            [stopKey]: {
                ...prev[stopKey],
                [field]: value
            }
        }));
    };

    // Function to handle city-level time update
    const updateCityTime = (cityIndex, time) => {
        citiesWithStops[cityIndex].cityTime = time;
        
        // Uncheck main stop if city time is manually set
        citiesWithStops[cityIndex].stops.forEach((stop, stopIndex) => {
            if (stop.isMainStop) {
                stop.isMainStop = false;
            }
        });
        setTimeout(() => {
            setCitiesWithStops([...citiesWithStops]);
        }, 100);
        // Notify parent component about the city-level time update
        onStopsUpdate([...citiesWithStops]);
    };

    // Function to handle city-level day update
    const updateCityDay = (cityIndex, day) => {
        citiesWithStops[cityIndex].cityDay = day;
        
        // Uncheck main stop if city day is manually set
        citiesWithStops[cityIndex].stops.forEach((stop, stopIndex) => {
            if (stop.isMainStop) {
                stop.isMainStop = false;
            }
        });
        
        setTimeout(() => {
            setCitiesWithStops([...citiesWithStops]);
        }, 100);
        // Notify parent component about the city-level day update
        onStopsUpdate([...citiesWithStops]);
    };

    const removeStopFromUI = (cityIndex, stopIndex) => {
        const wasMainStop = citiesWithStops[cityIndex].stops[stopIndex].isMainStop;
        const wasTripClosePoint = citiesWithStops[cityIndex].stops[stopIndex].tripClosePoint === 1;
        
        // Clean up editing state if this stop was being edited
        const stopKey = `${cityIndex}-${stopIndex}`;
        if (editingStops[stopKey]) {
            setEditingStops(prev => {
                const newState = { ...prev };
                delete newState[stopKey];
                return newState;
            });
        }
        
        // Use splice to properly remove the element from the array
        const updatedStops = [...citiesWithStops[cityIndex].stops];
        updatedStops.splice(stopIndex, 1);
        citiesWithStops[cityIndex].stops = updatedStops;
        
        // If we removed the main stop and there are still stops left, make the first remaining stop the main stop
        if (wasMainStop && citiesWithStops[cityIndex].stops.length > 0) {
            citiesWithStops[cityIndex].stops[0].isMainStop = true;
            // Update city-level time and day from the new main stop
            updateCityTimeAndDay(cityIndex, citiesWithStops[cityIndex].stops[0]);
        } else if (wasMainStop && citiesWithStops[cityIndex].stops.length === 0) {
            // If no stops left, clear city-level time and day
            citiesWithStops[cityIndex].cityTime = null;
            citiesWithStops[cityIndex].cityDay = 0;
        }
        
        // If we removed the trip close point, we need to select a new one from remaining stops
        if (wasTripClosePoint) {
            // Find the first available stop to set as trip close point
            let foundNewTripClosePoint = false;
            citiesWithStops.forEach((city, cIndex) => {
                if (!foundNewTripClosePoint) {
                    city.stops.forEach((stop, sIndex) => {
                        if (!foundNewTripClosePoint && stop && stop.isActive) {
                            stop.tripClosePoint = 1;
                            foundNewTripClosePoint = true;
                        } else if (stop) {
                            stop.tripClosePoint = 0;
                        }
                    });
                } else {
                    // Reset all other stops to 0
                    city.stops.forEach(stop => {
                        if (stop) {
                            stop.tripClosePoint = 0;
                        }
                    });
                }
            });
        }
        
        // Sort cities after removing a stop
        const sortedCities = sortCitiesByDayAndTiming(citiesWithStops);
        setCitiesWithStops(sortedCities);
        updateExpandedPanelsAfterSort(citiesWithStops, sortedCities);
    };

    const separateAddressLocation = (addressLocations) => {
        let addressLocation = [];
        let standardStops = [];
        addressLocations.forEach(location => {
            if (isValid(location.operator_code)) {
                addressLocation.push(location);
            } else {
                standardStops.push(location);
            }
        });
        
        return { addressLocation, standardStops };
    };

    const reSetStandaredCities = (city, cityIndex) => {
        dispatch(getAddressLocations([city], true, (data) => {
            standardStopsWithCities[citiesWithStops[cityIndex].city] = separateAddressLocation(data.addressLocations); 
            setStandardStopsWithCities({...standardStopsWithCities});
            setIsOpenNewStop(false);
        }, (errMsg) => {
            message.error(errMsg);
        }));
    };

    const onClickAddStop = (cityIndex, citystop) => {
        citiesWithStops[cityIndex].stops.push({
            isNewStop: true,
            isBoarding: citystop.isBoarding,
            isDrop: citystop.isDrop
        });
        
        // If this is the first stop for this city, make it the main stop
        if (citiesWithStops[cityIndex].stops.length === 1) {
            citiesWithStops[cityIndex].stops[0].isMainStop = true;
        }
        
        if (!isValidArray(standardStopsWithCities[citiesWithStops[cityIndex].displayName])) {
           reSetStandaredCities(citiesWithStops[cityIndex].displayName, cityIndex);
        }
        
        // Don't sort immediately - just update the state
        setCitiesWithStops([...citiesWithStops]);
    };

    const handleCityActiveChange = (value, cityIndex) => {
        const updatedCities = [...citiesWithStops];
        updatedCities[cityIndex].isActive = value;
        // Update all stops under this city
        updatedCities[cityIndex].stops.forEach(stop => {
            stop.isActive = value;
        });
        
        // Sort cities after changing city active status
        const sortedCities = sortCitiesByDayAndTiming(updatedCities);
        setCitiesWithStops(sortedCities);
        updateExpandedPanelsAfterSort(citiesWithStops, sortedCities);
    };

    const onClickStopCheckBox = (value, cityIndex, stopIndex) => {
        const updatedCities = [...citiesWithStops];
        updatedCities[cityIndex].stops[stopIndex].isActive = value;
        setCitiesWithStops([...updatedCities]);
    };

    const onClickStopEditCheckBox = (value, cityIndex, stopIndex) => {
        if (value) {
            // Initialize editing state with current stop values
            const stop = citiesWithStops[cityIndex].stops[stopIndex];
            const stopKey = `${cityIndex}-${stopIndex}`;
            setEditingStops(prev => ({
                ...prev,
                [stopKey]: {
                    timing: stop.timing,
                    day: stop.day,
                    phoneNumber: stop.phoneNumber,
                    selectedStopId: stop.selectedStopId,
                    displayName: stop.displayName
                }
            }));
        } else {
            // Clear editing state when exiting edit mode
            const stopKey = `${cityIndex}-${stopIndex}`;
            setEditingStops(prev => {
                const newState = { ...prev };
                delete newState[stopKey];
                return newState;
            });
        }
        citiesWithStops[cityIndex].stops[stopIndex].isEdit = value;
        setCitiesWithStops([...citiesWithStops]);
    };

    // Validate a single stop
    const validateStop = (stop, isNewStop = false) => {
        const errors = [];
        
        // For new stops, selectedStopId is required
        if (isNewStop && !stop.selectedStopId) {
            errors.push('Stop location is required');
        }
        
        // For existing stops, either selectedStopId or id is required
        if (!isNewStop && !stop.selectedStopId && !stop.id) {
            errors.push('Stop ID is required');
        }
        
        // Time is always required
        if (!stop.timing) {
            errors.push('Time is required');
        }
        
        // Day is always required and must be >= 0
        if (!isValidNumber(stop.day) || stop.day < 0) {
            errors.push('Day is required and must be >= 0');
        }
        
        // Phone number is optional for both new and existing stops
        
        return errors;
    };

    // Save a new stop
    const handleSaveNewStop = (cityIndex, stopIndex) => {
        const stop = citiesWithStops[cityIndex].stops[stopIndex];
        const errors = validateStop(stop, true);
        
        if (errors.length > 0) {
            message.error(`Please fix the following errors: ${errors.join(', ')}`);
            setShowError(true);
            return;
        }
        
        // Mark as being saved
        const stopKey = `${cityIndex}-${stopIndex}`;
        setSavingStops(prev => new Set(prev).add(stopKey));
        
        // Save operation
        // setTimeout(() => {
            // Convert new stop to existing stop
            citiesWithStops[cityIndex].stops[stopIndex].isNewStop = false;
            citiesWithStops[cityIndex].stops[stopIndex].isSaved = true;
            
            // Ensure the stop has all required properties for existing stops
            if (citiesWithStops[cityIndex].stops[stopIndex].selectedStopId) {
                // This stop was selected from dropdown, ensure it has proper structure
                const selectedStop = citiesWithStops[cityIndex].stops[stopIndex];
                citiesWithStops[cityIndex].stops[stopIndex] = {
                    ...selectedStop,
                    id: selectedStop.selectedStopId, // Add id for existing stops
                    isNewStop: false,
                    isSaved: true
                };
            }
            
            // Sort cities by day and timing after saving
            const sortedCities = sortCitiesByDayAndTiming(citiesWithStops);
            setCitiesWithStops(sortedCities);
            updateExpandedPanelsAfterSort(citiesWithStops, sortedCities);
            // Notify parent component about the update
            onStopsUpdate(sortedCities);
            
            setSavingStops(prev => {
                const newSet = new Set(prev);
                newSet.delete(stopKey);
                return newSet;
            });
            
            message.success('Stop saved successfully!');
            setShowError(false);
        // }, 1000);
    };

    // Save an edited stop
    const handleSaveEditedStop = (cityIndex, stopIndex) => {
        const stopKey = `${cityIndex}-${stopIndex}`;
        const editingData = editingStops[stopKey];
        
        if (!editingData) {
            message.error('No changes to save');
            return;
        }
        
        // Create a copy of the stop with editing changes applied
        const updatedStop = {
            ...citiesWithStops[cityIndex].stops[stopIndex],
            ...editingData
        };
        
        const errors = validateStop(updatedStop, false);
        
        if (errors.length > 0) {
            message.error(`Please fix the following errors: ${errors.join(', ')}`);
            setShowError(true);
            return;
        }
        
        // Mark as being saved
        setSavingStops(prev => new Set(prev).add(stopKey));
        
        // Apply all changes from editing state to the actual stop
        Object.keys(editingData).forEach(field => {
            updateStopsDate(editingData[field], cityIndex, stopIndex, field);
        });
        
        // Exit edit mode
        citiesWithStops[cityIndex].stops[stopIndex].isEdit = false;
        citiesWithStops[cityIndex].stops[stopIndex].isSaved = true;
        
        // Sort cities by day and timing after saving
        const sortedCities = sortCitiesByDayAndTiming(citiesWithStops);
        setCitiesWithStops(sortedCities);
        updateExpandedPanelsAfterSort(citiesWithStops, sortedCities);
        // Notify parent component about the update
        onStopsUpdate(sortedCities);
        
        // Clear editing state
        setEditingStops(prev => {
            const newState = { ...prev };
            delete newState[stopKey];
            return newState;
        });
        
        setSavingStops(prev => {
            const newSet = new Set(prev);
            newSet.delete(stopKey);
            return newSet;
        });
        
        message.success('Stop updated successfully!');
        setShowError(false);
    };

    // Cancel editing a stop
    const handleCancelEditStop = (cityIndex, stopIndex) => {
        const stopKey = `${cityIndex}-${stopIndex}`;
        
        // Exit edit mode without saving
        citiesWithStops[cityIndex].stops[stopIndex].isEdit = false;
        setCitiesWithStops([...citiesWithStops]);
        
        // Clear editing state
        setEditingStops(prev => {
            const newState = { ...prev };
            delete newState[stopKey];
            return newState;
        });
        
        setShowError(false);
        message.info('Edit cancelled - changes discarded');
    };

    const printAdressLocation = (city, cityIndex) => {
        let normalStops = [];
        if (isValid(standardStopsWithCities) && isValid(standardStopsWithCities[city])) {
            let addressLocation = standardStopsWithCities[city].addressLocation;
            if (isValidArray(addressLocation) && addressLocation.length > 0) {
                // Get all selected stop IDs for this city to filter them out
                const selectedStopIds = citiesWithStops[cityIndex]?.stops?.map(stop => stop.selectedStopId).filter(id => id) || [];
                
                addressLocation.map((address) => {
                    // Only add if this stop is not already selected
                    if (!selectedStopIds.includes(address.id)) {
                        normalStops.push(<Option key={address.id} name={address.name} id={address.id} value={address.id} label={address.name} data={address}>{address.name}</Option>);
                    }
                });
            }
        }
        return normalStops;
    };

    const printStandardStops = (city, cityIndex) => {
        let standardStopsOptions = [];
        if (isValid(standardStopsWithCities) && isValid(standardStopsWithCities[city])) {
            let standardStops = standardStopsWithCities[city].standardStops;
            if (isValidArray(standardStops) && standardStops.length > 0) {
                // Get all selected stop IDs for this city to filter them out
                const selectedStopIds = citiesWithStops[cityIndex]?.stops?.map(stop => stop.selectedStopId).filter(id => id) || [];
                
                standardStops.map((address) => {
                    // Only add if this stop is not already selected
                    if (!selectedStopIds.includes(address.id)) {
                        standardStopsOptions.push(<Option key={address.id} name={address.name} id={address.id} value={address.id} label={address.name} data={address}>{address.name}</Option>);
                    }
                });
            }
        }
        return standardStopsOptions;
    };

    const renderNewStopRow = (cityIndex, stopIndex, city, stop) => {
        let stopExits = false, dayExits = false, timeExits = false;
        if (isValid(stop.selectedStopId)) {
            stopExits = true;
        }
        if (isValidNumber(stop.day) && stop.day >= 0) {
            dayExits = true;
        }
        if (isValid(stop.timing)) {
            timeExits = true;
        }

        const isSaving = savingStops.has(`${cityIndex}-${stopIndex}`);
        
        return (
            <div className={`new-timing-tab-block ${isSaving ? 'saving-stop' : ''}`}>
                <Row className={'stop-row'} gutter={0} justifyContent="space-around">
                    {isMobile ? null : <Col span={4}></Col>}
                    <Col span={isMobile ? 12 : 4} className="stop-name-col">
                        <div className="stop-city-col">
                            <Row className="description-row">
                                <Col span={isMobile ? 3 : 4}>
                                    {renderCheckBox(stop.isActive !== undefined ? stop.isActive : 1, cityIndex, stopIndex)}
                                </Col>
                                <Col span={16} offset={0} style={{ marginLeft: '8px' }}>
                                    <Select
                                        showSearch
                                        optionFilterProp="children"
                                        size="small"
                                        placeholder="Select stop"
                                        className={showError && !stopExits ? "selectStop errorInput" : "selectStop"}
                                        value={isValid(stop.selectedStopId) ? { value: stop.selectedStopId, label: stop.displayName } : null}
                                        dropdownRender={menu => (
                                            <div className="createStopDiv">
                                                {menu}
                                                <Divider style={{ margin: '8px 0' }} />
                                                <Button
                                                    className="createStopBtn"
                                                    size="small"
                                                    onClick={() => {
                                                        setSelectedCity(city);
                                                        setSelectedCityIndex(cityIndex);
                                                        setSelectedStopIndex(stopIndex);
                                                        setIsOpenNewStop(true);
                                                    }}
                                                >
                                                    Create New Stop
                                                </Button>
                                            </div>
                                        )}
                                        onSelect={(value, data) => {
                                            updateStopsDate(data.id, cityIndex, stopIndex, 'selectedStopId', data);
                                        }}
                                        style={{ width: '100%' }}
                                        labelInValue={true}
                                    >
                                        {printAdressLocation(city, cityIndex)}
                                        <Select.OptGroup label="Standard stops">{printStandardStops(city, cityIndex)}</Select.OptGroup>
                                    </Select>
                                </Col>
                            </Row>
                        </div>
                    </Col>
                    <Col span={isMobile ? 6 : null} className="time-col-in-stops">
                        <div style={{ 
                            width: isMobile ? '80px' : '130px',
                            marginRight: '10px'
                        }}>
                            <TimePickerComponent
                                value={stop.timing}
                                onSelect={(date) => {
                                    let time = date.format("HH:mm:ss");
                                    updateStopsDate(time, cityIndex, stopIndex, 'timing');
                                }}
                                showError={showError}
                                timeExits={timeExits}
                                isMobile={isMobile}
                            />
                        </div>
                    </Col>
                    <Col span={isMobile ? 2 : 4} className="day-col">
                        <div style={{ width: isMobile ? '32px' : '30px' }}>
                            <DayInputComponent
                                value={stop.day + 1}
                                onChange={(value) => {
                                    updateStopsDate(value - 1, cityIndex, stopIndex, 'day');
                                }}
                                showError={showError}
                                dayExits={dayExits}
                                isMobile={isMobile}
                            />
                        </div>
                    </Col>
                    <Col span={isMobile ? 2 : 4} className="main-stop-col">
                        <div style={{ 
                            width: isMobile ? '32px' : '30px'
                        }}>
                            <Button
                                type="text"
                                size="small"
                                style={{ 
                                    padding: 0,
                                    height: 'auto',
                                    border: 'none',
                                    backgroundColor: 'transparent'
                                }}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    const updatedStops = updateMainStopForCity(citiesWithStops, cityIndex, stopIndex, true);
                                    setCitiesWithStops(updatedStops);
                                }}
                            >
                                {stop.isMainStop ? (
                                    <CheckSquareFilled 
                                        style={{ 
                                            fontSize: '18px',
                                            color: '#52c41a'
                                        }}
                                    />
                                ) : (
                                    <div 
                                        style={{ 
                                            width: '18px',
                                            height: '18px',
                                            border: '2px solid #d9d9d9',
                                            borderRadius: '2px',
                                            backgroundColor: 'white'
                                        }}
                                    />
                                )}
                            </Button>
                        </div>
                    </Col>
                    <Col span={isMobile ? 2 : 4} className="action-col">
                        <div className="action-wrapper">
                            <div className="trip-close-checkbox">
                                <Button
                                    type="text"
                                    size="small"
                                    style={{ 
                                        padding: 0,
                                        height: 'auto',
                                        border: 'none',
                                        backgroundColor: 'transparent'
                                    }}
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        updateStopsDate(1, cityIndex, stopIndex, 'tripClosePoint');
                                    }}
                                    disabled={!stop.isActive || temporaryCityFlow}
                                >
                                    {stop.tripClosePoint === 1 ? (
                                        <div className="tick-icon">
                                            <Icon component={() => (<img src={TicMarkIcon} style={{width:'14px'}} />)} />
                                        </div>
                                    ) : (
                                        <div 
                                            style={{ 
                                                width: '14px',
                                                height: '14px',
                                                border: '2px solid #d9d9d9',
                                                borderRadius: '50%',
                                                backgroundColor: 'white'
                                            }}
                                        />
                                    )}
                                </Button>
                            </div>
                            <Button
                                type="text"
                                className="outline-button edit-col"
                                icon={<SaveOutlined />}
                                loading={savingStops.has(`${cityIndex}-${stopIndex}`)}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    handleSaveNewStop(cityIndex, stopIndex);
                                }}
                                style={{ marginRight: '4px' }}
                            />
                            <Button
                                type="text"
                                className="outline-button edit-col"
                                icon={<DeleteOutlined />}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    removeStopFromUI(cityIndex, stopIndex);
                                }}
                            />
                        </div>
                    </Col>
                </Row>
                <Row className="mobile-phone-row margin-bottom-10">
                    <Col offset={4}>
                        <InputNumber
                            placeholder="Phone Number (Optional)"
                            className="phoneInput"
                            size="small"
                            value={stop.phoneNumber}
                            onChange={(value) => {
                                updateStopsDate(value, cityIndex, stopIndex, 'phoneNumber');
                            }}
                            style={{ width: '100%', minWidth: '100px' }}
                        />
                    </Col>
                </Row>
            </div>
        );
    };

    const renderRow = (stop, cityIndex, stopIndex) => {
        let className = '';
        
        if (stop.isActive == 0) {
            className = 'inactive-stop';
        }
        
        if (stop.isSaved) {
            className += ' saved-stop';
        }
        
        if (savingStops.has(`${cityIndex}-${stopIndex}`)) {
            className += ' saving-stop';
        }

        // Get editing state values if in edit mode
        const stopKey = `${cityIndex}-${stopIndex}`;
        const editingData = editingStops[stopKey] || {};
        
        // Use editing values if in edit mode, otherwise use actual stop values
        const displayTiming = stop.isEdit ? editingData.timing : stop.timing;
        const displayDay = stop.isEdit ? editingData.day : stop.day;
        const displayPhoneNumber = stop.isEdit ? editingData.phoneNumber : stop.phoneNumber;
        const displaySelectedStopId = stop.isEdit ? editingData.selectedStopId : stop.selectedStopId;
        const displayName = stop.isEdit ? editingData.displayName : stop.displayName;

        // Check if fields are valid when in edit mode
        const timeExits = isValid(displayTiming);
        const dayExits = isValidNumber(displayDay) && displayDay >= 0;
        const showFieldError = showError && stop.isEdit;

        const renderTimingColumn = () => {
            if (stop.isEdit) {
                return (
                    <div style={{ 
                        width: isMobile ? '80px' : '130px',
                        marginRight: '10px'
                    }}>
                        <TimePickerComponent
                            value={displayTiming}
                            onSelect={(date) => {
                                let time = date.format("HH:mm:ss");
                                updateEditingStop(cityIndex, stopIndex, 'timing', time);
                            }}
                            showError={showFieldError}
                            timeExits={timeExits}
                            isMobile={isMobile}
                        />
                    </div>
                );
            }

            return (
                <div style={{ 
                    width: isMobile ? '100px' : '130px',
                    marginRight: '10px'
                }}>
                    {isValid(displayTiming) ? getTimeStampInReadableFormat(displayTiming) : '--'}
                    {Array.isArray(stop.timings) && stop.timings.length > 1 && (
                        <Tooltip 
                            title={stop.timings.map(t => 
                                `${Array.isArray(t.tripStartDates) ? 
                                    t.tripStartDates.map(date => date).join(', ') : 
                                    t.tripStartDates}: ${isValid(t.timing) ? getTimeStampInReadableFormat(t.timing) : '--'}`
                            ).join('\n')}
                            placement="right"
                        >
                            <div style={{ color: '#1B6BE3', fontSize: '12px' }}> (Multiple)</div>
                        </Tooltip>
                    )}
                </div>
            );
        };

        return (
            <div>
                <Row className={`stop-row ${className}`} gutter={0}>
                    {isMobile ? null : <Col span={4}></Col>}
                    <Col span={isMobile ? 12 : 4} className="stop-name-col">
                        <div className="stop-city-col">
                            <Row className="description-row">
                                <Col span={isMobile ? 3 : 4}>
                                    {renderCheckBox(stop.isActive, cityIndex, stopIndex)}
                                </Col>
                                <Col span={16} offset={0} style={{ marginLeft: '8px' }}>
                                    <StopNameDisplay 
                                        displayName={displayName || 'Unknown Stop'}
                                        isBoarding={stop.isBoarding}
                                        isDrop={stop.isDrop}
                                    />
                                </Col>
                            </Row>
                        </div>
                    </Col>
                    <Col span={isMobile ? 6 : null} className="time-col-in-stops" >
                        {renderTimingColumn()}
                    </Col>
                    <Col span={isMobile ? 2 : 4} className="day-col">
                        <div style={{ 
                            width: isMobile ? '32px' : '30px'
                        }}>
                            {stop.isEdit ?
                                <DayInputComponent
                                    value={displayDay + 1}
                                    onChange={(value) => {
                                        updateEditingStop(cityIndex, stopIndex, 'day', value - 1);
                                    }}
                                    showError={showFieldError}
                                    dayExits={dayExits}
                                    isMobile={isMobile}
                                />
                                :
                                <div className="day-display">
                                    {displayDay + 1}
                                </div>
                            }
                        </div>
                    </Col>
                    <Col span={isMobile ? 2 : 4} className="main-stop-col">
                        <div style={{ 
                            width: isMobile ? '32px' : '30px'
                        }}>
                            <Button
                                type="text"
                                size="small"
                                style={{ 
                                    padding: 0,
                                    height: 'auto',
                                    border: 'none',
                                    backgroundColor: 'transparent'
                                }}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    const updatedStops = updateMainStopForCity(citiesWithStops, cityIndex, stopIndex, true);
                                    setCitiesWithStops(updatedStops);
                                }}
                                disabled={!stop.isActive}
                            >
                                {stop.isMainStop ? (
                                    <CheckSquareFilled 
                                        style={{ 
                                            fontSize: '18px',
                                            color: '#52c41a'
                                        }}
                                    />
                                ) : (
                                    <div 
                                        style={{ 
                                            width: '18px',
                                            height: '18px',
                                            border: '2px solid #d9d9d9',
                                            borderRadius: '2px',
                                            backgroundColor: 'white'
                                        }}
                                    />
                                )}
                            </Button>
                        </div>
                    </Col>
                    <Col span={isMobile ? 2 : 4} className="action-col">
                        <div className="action-wrapper">
                            <div className="trip-close-checkbox">
                                <Button
                                    type="text"
                                    size="small"
                                    style={{ 
                                        padding: 0,
                                        height: 'auto',
                                        border: 'none',
                                        backgroundColor: 'transparent'
                                    }}
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        updateStopsDate(1, cityIndex, stopIndex, 'tripClosePoint');
                                    }}
                                    disabled={!stop.isActive || temporaryCityFlow}
                                >
                                    {stop.tripClosePoint === 1 ? (
                                        <div className="tick-icon">
                                            <Icon component={() => (<img src={TicMarkIcon} style={{width:'14px'}} />)} />
                                        </div>
                                    ) : (
                                        <div 
                                            style={{ 
                                                width: '14px',
                                                height: '14px',
                                                border: '2px solid #d9d9d9',
                                                borderRadius: '50%',
                                                backgroundColor: 'white'
                                            }}
                                        />
                                    )}
                                </Button>
                            </div>
                            {stop.isEdit ? (
                                <>
                                    <Button
                                        type="text"
                                        className="outline-button edit-col"
                                        icon={<SaveOutlined />}
                                        loading={savingStops.has(`${cityIndex}-${stopIndex}`)}
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            handleSaveEditedStop(cityIndex, stopIndex);
                                        }}
                                        style={{ marginRight: '4px' }}
                                    />
                                    <Button
                                        type="text"
                                        className="outline-button edit-col"
                                        icon={<CloseOutlined />}
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            handleCancelEditStop(cityIndex, stopIndex);
                                        }}
                                        style={{ marginRight: '4px' }}
                                    />
                                </>
                            ) : (
                                <Button
                                    type="text" 
                                    className={`outline-button edit-col ${isMobile ? 'edit-col-mobile' : ''}`} 
                                    icon={<EditOutlined />}
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        onClickStopEditCheckBox(true, cityIndex, stopIndex)
                                    }}
                                />
                            )}
                        </div>
                    </Col>
                </Row>
                {stop.isEdit && (
                    <Row className="mobile-phone-row margin-bottom-10">
                        <Col offset={4}>
                            <InputNumber
                                placeholder="Phone Number (Optional)"
                                className="phoneInput"
                                size="small"
                                value={displayPhoneNumber}
                                onChange={(value) => {
                                    updateEditingStop(cityIndex, stopIndex, 'phoneNumber', value);
                                }}
                                style={{ width: '100%', minWidth: '100px' }}
                            />
                        </Col>
                    </Row>
                )}
            </div>
        )
    };

    const updateMainStopForCity = (citiesWithStops, cityIndex, stopIndex, value) => {
        return citiesWithStops.map((city, cIndex) => {
            if (cIndex === cityIndex) {
                const updatedStops = city.stops.map((stop, sIndex) => ({
                    ...stop,
                    isMainStop: sIndex === stopIndex ? value : false
                }));
                
                // If setting a main stop, update city-level time and day
                if (value && updatedStops[stopIndex]) {
                    const mainStop = updatedStops[stopIndex];
                    return {
                        ...city,
                        stops: updatedStops,
                        cityTime: mainStop.timing,
                        cityDay: mainStop.day
                    };
                }
                
                // If unsetting a main stop, check if there are any other main stops
                // If no main stops remain, clear city-level time and day
                const hasAnyMainStop = updatedStops.some(stop => stop.isMainStop);
                if (!hasAnyMainStop) {
                    return {
                        ...city,
                        stops: updatedStops,
                        cityTime: null,
                        cityDay: 0
                    };
                }
                return {
                    ...city,
                    stops: updatedStops
                };
            }
            return city;
        });
    };

    const handleExpandAll = () => {
        const allPanelKeys = citiesWithStops.map((_, index) => index.toString());
        setExpandedPanels(allPanelKeys);
    };

    const handleCollapseAll = () => {
        setExpandedPanels([]);
    };

    const handlePanelChange = (keys) => {
        setExpandedPanels(keys);
    };

    const renderCheckBox = (value = true, cityIndex, stopIndex) => {
        if (!value) {
            return (
                <MinusSquareFilled 
                    className="marginRight"
                    style={{ 
                        color: '#ff4d4f',
                        fontSize: '18px',
                        cursor: 'pointer'
                    }}
                    onClick={(e) => {
                        e.stopPropagation();
                        onClickStopCheckBox(true, cityIndex, stopIndex);
                    }}
                />
            );
        }
        return (
            <CheckSquareFilled 
                className="marginRight default-checkbo-btn"
                style={{ 
                    fontSize: '18px',
                    cursor: 'pointer'
                }}
                onClick={(e) => {
                    e.stopPropagation();
                    onClickStopCheckBox(false, cityIndex, stopIndex);
                }}
            />
        );
    };

    const renderAllCitiesWithStops = () => {
        let components = [];
        if (isValidArray(citiesWithStops) && citiesWithStops.length > 0) {
            citiesWithStops.forEach((citystop, cityIndex) => {
                let {
                    stops,
                    displayName
                } = citystop;

                // Temp flow: existing cities are shown read-only; only the new city is editable
                const isLockedCity = temporaryCityFlow
                    && citystop.city !== temporaryCityName
                    && displayName !== temporaryCityName;
                
                let collapseAbleComponent = [];
                
                // Render existing stops if any
                if (isValidArray(stops) && stops.length > 0) {
                    stops.forEach((stop, stopIndex) => {
                        if (stop.isNewStop == true) {
                            collapseAbleComponent.push(renderNewStopRow(cityIndex, stopIndex, displayName, stop));
                        } else {
                            collapseAbleComponent.push(renderRow(stop, cityIndex, stopIndex));
                        }
                    });
                }
                
                // Always render the city panel, even if it has no stops
                components.push(
                    <div key={`city-panel-${cityIndex}`} style={isLockedCity ? { pointerEvents: 'none', opacity: 0.6 } : undefined}>
                    <Collapse
                        collapsible={displayName}
                        ghost
                        className="stop-collapse new-timing-body"
                        activeKey={expandedPanels}
                        onChange={handlePanelChange}
                        expandIcon={({ isActive }) => (
                            <span style={{ display: 'flex', alignItems: 'center' }}>
                                <CityCheckbox 
                                    isActive={citystop.isActive}
                                    cityIndex={cityIndex}
                                    onToggle={handleCityActiveChange}
                                    isShowCheckBox={true}
                                />
                                {isActive ? <MinusSquareOutlined /> : <PlusSquareOutlined />}
                            </span>
                        )}
                    >
                        <Panel 
                            key={cityIndex}
                            header={
                                <Row style={{ width: '100%', alignItems: 'center' }}>
                                    <Col span={isMobile ? 8 : 6}>
                                        <span style={{ display: 'flex', alignItems: 'center' }}>
                                            {displayName}
                                            {(citystop.isBoarding || citystop.isDrop) && (
                                                <span style={{ marginLeft: '8px' }}>
                                                    {citystop.isBoarding && (
                                                        <span style={{ 
                                                            backgroundColor: '#1890ff',
                                                            color: 'white',
                                                            padding: '0 4px',
                                                            borderRadius: '2px',
                                                            fontSize: '12px',
                                                            marginRight: '4px'
                                                        }}>
                                                            B
                                                        </span>
                                                    )}
                                                    {citystop.isDrop && (
                                                        <span style={{ 
                                                            backgroundColor: '#52c41a',
                                                            color: 'white',
                                                            padding: '0 4px',
                                                            borderRadius: '2px',
                                                            fontSize: '12px'
                                                        }}>
                                                            D
                                                        </span>
                                                    )}
                                                </span>
                                            )}
                                        </span>
                                    </Col>
                                    <Col span={isMobile ? 6 : 4}>
                                        <div style={{ 
                                            width: isMobile ? '80px' : '100px',
                                            marginRight: '10px'
                                        }}>
                                            <TimePickerComponent
                                                value={citystop.cityTime}
                                                onSelect={(date) => {
                                                    let time = date.format("HH:mm:ss");
                                                    updateCityTime(cityIndex, time);
                                                }}
                                                showError={false}
                                                timeExits={true}
                                                isMobile={isMobile}
                                            />
                                        </div>
                                    </Col>
                                    <Col span={isMobile ? 4 : 2}>
                                        <div style={{ width: isMobile ? '50px' : '60px' }}>
                                            <DayInputComponent
                                                value={citystop.cityDay + 1}
                                                onChange={(value) => {
                                                    updateCityDay(cityIndex, value - 1);
                                                }}
                                                showError={false}
                                                dayExits={true}
                                                isMobile={isMobile}
                                            />
                                        </div>
                                    </Col>
                                    <Col span={isMobile ? 6 : 12}>
                                        <span style={{ fontSize: '12px', color: '#666' }}>
                                            {citystop.cityTime ? 'City Time/Day' : 'Set city time/day'}
                                        </span>
                                    </Col>
                                </Row>
                            }
                        >
                            {collapseAbleComponent}
                            <Row>
                                <Col span={4}></Col>
                                <Col>
                                    <div>
                                        <Button className="addStopBtn" size="small" onClick={(e) => {
                                            e.stopPropagation();
                                            onClickAddStop(cityIndex, citystop);
                                        }}>Add stop</Button>
                                    </div>
                                </Col>
                            </Row>
                        </Panel>
                    </Collapse>
                    </div>
                );
            })
        }
        return components;
    };

    const handleContinue = () => {
        // Check that every city has at least one stop
        const allCitiesHaveStops = citiesWithStops.every(city => city.stops && city.stops.length > 0);
        if (!allCitiesHaveStops) {
            message.error("Every city must have at least one stop before continuing. Please add a stop to each city.");
            setShowError(true);
            return;
        }

        // Check if there are any unsaved new stops
        const hasUnsavedNewStops = citiesWithStops.some(city => 
            city.stops.some(stop => stop.isNewStop && !stop.isSaved)
        );

        if (hasUnsavedNewStops) {
            message.error("Please save all new stops before continuing. Click the save button (✓) next to each new stop.");
            setShowError(true);
            return;
        }

        // Validate all fields first
        let mainStopError = false;
        let tripClosePointError = false;
        const errors = [];

        try {
            // Check if exactly one trip close point is selected across all stops
            let tripClosePoints = 0;
            citiesWithStops.forEach((city) => {
                city.stops.forEach(stop => {
                    if (stop.tripClosePoint === 1) {
                        tripClosePoints++;
                    }
                });
            });
            
            if (tripClosePoints !== 1) {
                tripClosePointError = true;
            }

            citiesWithStops.forEach((city, cityIndex) => {
                // Check if each city has exactly one main stop
                // Skip this check if the city has valid time and day set (manual override)
                const hasValidCityTimeAndDay = isValid(city.cityTime) && isValidNumber(city.cityDay) && city.cityDay >= 0;
                
                if (!hasValidCityTimeAndDay) {
                    const mainStops = city.stops.filter(stop => stop.isMainStop);
                    if (city.stops.length > 0 && mainStops.length !== 1) {
                        mainStopError = true;
                    }
                }
                
                city.stops.forEach(stop => {
                    // Skip validation for stops that are being saved or have been saved
                    if (stop.isSaved || savingStops.has(`${cityIndex}-${city.stops.indexOf(stop)}`)) {
                        return;
                    }

                    // For new stops, selectedStopId is required
                    if (stop.isNewStop && !stop.selectedStopId) {
                        errors.push('Stop location is required');
                    }
                    
                    // For existing stops, either selectedStopId or id is required
                    if (!stop.isNewStop && !stop.selectedStopId && !stop.id) {
                        errors.push('Stop ID is required');
                    }
                    
                    // Time is always required
                    if (!stop.timing) {
                        errors.push('Time is required');
                    }
                    
                    // Day is always required and must be >= 0
                    if (!isValidNumber(stop.day) || stop.day < 0) {
                        errors.push('Day is required and must be >= 0');
                    }
                    
                    // Phone number is optional for both new and existing stops
                });
            });
            
            if (errors.length > 0) {
                message.error(`Please fix the following errors: ${errors.join(', ')}`);
                setShowError(true);
                return;
            }

            // Check for main stop and trip close point errors
            if (mainStopError) {
                message.error("Each city must have exactly one main stop selected, unless city-level time and day are manually set.");
                setShowError(true);
                return;
            }

            if (tripClosePointError) {
                message.error("Exactly one trip close point must be selected across all stops.");
                setShowError(true);
                return;
            }

        } catch (e) {
            console.error("Error validating stops:", e);
            message.error("An unexpected error occurred while validating stops.");
            setShowError(true);
            return;
        }

        // If all validations pass, proceed to continue
        onStopsUpdate(citiesWithStops);
        onContinue();
    };

    return (
        <ZoomComponent isMobile={isMobile}>
            <div className={`time-method-stops-root ${customClassName || ''}`} style={{ width: '100%' }}>
                <Row>
                    <Col>
                        <Title level={3}>
                            Manage Stops
                        </Title>
                    </Col>
                </Row>
                <Row>
                    <Col>
                        <Title level={5}>
                            Add and manage stops for your trip
                        </Title>
                    </Col>
                </Row>
                
                {/* Show instruction when no existing stops */}
                {(!isValidArray(existingStops) || existingStops.length === 0) && (
                    <Row style={{ marginBottom: '20px' }}>
                        <Col>
                            <Text style={{ color: '#666', fontSize: '14px' }}>
                                <strong>Instructions:</strong> Click on each city below and use the "Add stop" button to create stops for your trip. 
                                Each stop requires a location, time, day, and phone number.
                            </Text>
                        </Col>
                    </Row>
                )}

                {/* Show stops section and buttons if citiesWithStops has data */}
                {isValidArray(citiesWithStops) && citiesWithStops.length > 0 && (
                    <>
                        <Row className="heading-new-timing" align="middle">
                            {isMobile ? null : <Col span={4}></Col>}
                            <Col span={isMobile ? 12 : 4} style={{ display: 'flex', alignItems: 'center' }}>
                                <span style={{ marginRight: 8, cursor: 'pointer', fontSize: 18 }}
                                    onClick={() => {
                                        if (expandedPanels.length === citiesWithStops.length) {
                                            handleCollapseAll();
                                        } else {
                                            handleExpandAll();
                                        }
                                    }}
                                >
                                    {expandedPanels.length === citiesWithStops.length ? <MinusSquareOutlined /> : <PlusSquareOutlined />}
                                </span>
                                <div className="city-col">Stop</div>
                            </Col>
                            <Col span={isMobile ? 6 : null}>
                                <div style={{ 
                                    width: isMobile ? '80px' : '130px',
                                    marginRight: '10px'
                                }}>
                                    Time
                                </div>
                            </Col>
                            <Col span={isMobile ? 2 : 4}>
                                <div style={{ width: isMobile ? '35px' : '30px' }}>
                                    Day
                                </div>
                            </Col>
                            <Col span={isMobile ? 2 : 4}>
                                <div style={{ width: isMobile ? '35px' : '30px' }}>
                                    Main Stop
                                </div>
                            </Col>
                            <Col span={isMobile ? 2 : 4}>
                                <div className="">
                                    {isMobile ? "TC" : "Trip Close"}
                                </div>
                            </Col>
                        </Row>
                        <Divider className="dividerCol" />
                        <div className="new-timing-body">
                            {renderAllCitiesWithStops()}
                        </div>
                        <Row justify="center" style={{ marginTop: '20px' }}>
                            <Button 
                                type="primary" 
                                onClick={handleContinue}
                            >
                                Continue to Verification
                            </Button>
                        </Row>
                    </>
                )}

                <Modal
                    title={null} 
                    className="createNewStopModal"
                    modalTitle="Delay Notification"
                    visible={isOpenNewStop} 
                    width="95vw"
                    onOk={() => {
                        setIsOpenNewStop(false)
                    }} 
                    onCancel={() => {
                        setIsOpenNewStop(false)
                    }} 
                    centered={true}
                    footer={null}
                    cancelButtonProps={{className:"exit-btn"}}
                >
                    <div>
                        <CreateStandardStopsPage 
                            fromModal={true} 
                            isAddressBook={true}
                            selectedCity={selectedCity} 
                            onSuccessRefresh={(id, savedRecord) => {
                                reSetStandaredCities(selectedCity, selectedCityIndex);
                                // Shape the freshly created address like a stop-dropdown option so
                                // updateStopsDate wires it into the panel exactly as a manual pick would.
                                const newStopOption = isValid(savedRecord) ? {
                                    id: id,
                                    name: savedRecord.name,
                                    value: id,
                                    label: savedRecord.name,
                                    data: savedRecord
                                } : null;
                                updateStopsDate(id, selectedCityIndex, selectedStopIndex, 'selectedStopId', newStopOption);
                            }}
                            operatorCode={global.operatorCode}
                            onFailure={(errMsg) => {
                                message.error(errMsg);
                            }}
                            cancelModal={()=>{
                                setIsOpenNewStop(false);
                            }}
                        />
                    </div>
                </Modal>
            </div>
        </ZoomComponent>
    );
}; 